import { useTranslation } from 'react-i18next'
import { Avatar } from '@/components/ui/avatar'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { scheduleOf } from '../schedule'
import type { Discipline, ModuleRequirement, PricingType, ProjectColor, ProjectIconKey, ProjectKind, ProjectStatus } from '../types'
import { PROJECT_COLOR, PROJECT_ICON } from '../visuals'
import { usePriceFormat } from '../usePriceFormat'

const RECURRING: Exclude<PricingType, 'one_time'>[] = ['monthly', 'yearly']

export function PriceLine({ amounts }: { amounts: Record<PricingType, string> }) {
  const { t } = useTranslation()
  const fmtPrice = usePriceFormat()
  const recurring = RECURRING.filter((k) => Number(amounts[k]) > 0)
  return (
    <span className="tabular-nums">
      {fmtPrice(amounts.one_time)}
      {recurring.map((k) => (
        <span key={k} className="text-xs font-normal text-muted-foreground">
          {' + '}
          {fmtPrice(amounts[k])} {t(`projects.pricingSuffix.${k}`)}
        </span>
      ))}
    </span>
  )
}

export function ProjectIcon({ icon, color, className }: { icon: ProjectIconKey; color: ProjectColor; className?: string }) {
  const Icon = PROJECT_ICON[icon]
  return (
    <span className={cn('flex size-12 shrink-0 items-center justify-center rounded-xl shadow-sm', PROJECT_COLOR[color], className)}>
      <Icon className="size-[45%]" aria-hidden />
    </span>
  )
}

const PROJECT_STATUS_TONE: Record<ProjectStatus, BadgeTone> = { active: 'success', paused: 'warning', completed: 'info', archived: 'neutral' }
const PROJECT_STATUS_DOT: Record<ProjectStatus, string> = { active: 'bg-success', paused: 'bg-warning', completed: 'bg-info', archived: 'bg-muted-foreground' }

export function ProjectStatusBadge({ status }: { status: ProjectStatus }) {
  const { t } = useTranslation()
  return (
    <Badge tone={PROJECT_STATUS_TONE[status]}>
      <span className={cn('size-1.5 rounded-full', PROJECT_STATUS_DOT[status])} aria-hidden />
      {t(`projects.status.${status}`)}
    </Badge>
  )
}

/** Bitiş tarihine kalan süre ya da gecikme; bitişsiz projede gösterilmez. */
export function ScheduleBadge({ endDate, status }: { endDate: string; status: ProjectStatus }) {
  const { t } = useTranslation()
  const s = scheduleOf(endDate, status)
  if (!s) return null
  const tone: BadgeTone = s.state === 'late' ? 'danger' : s.state === 'today' || s.days <= 7 ? 'warning' : 'neutral'
  const label = s.state === 'late' ? t('projects.schedule.daysLate', { count: s.days }) : s.state === 'today' ? t('projects.schedule.dueToday') : t('projects.schedule.daysLeft', { count: s.days })
  return <Badge tone={tone}>{label}</Badge>
}

export function ProjectKindBadge({ kind }: { kind: ProjectKind }) {
  const { t } = useTranslation()
  return <Badge tone={kind === 'custom' ? 'info' : 'neutral'}>{t(`projects.kind.${kind}`)}</Badge>
}

const DISCIPLINE_CLASS: Record<Discipline, string> = {
  management: 'bg-muted text-muted-foreground',
  frontend: 'bg-info/12 text-info',
  backend: 'bg-violet/12 text-violet',
  qa: 'bg-warning/15 text-warning-strong',
  installation: 'bg-success/12 text-success',
  design: 'bg-destructive/12 text-destructive',
  sales: 'bg-brand-orange/12 text-brand-orange',
}

export function DisciplineBadge({ discipline }: { discipline: Discipline }) {
  const { t } = useTranslation()
  return <Badge className={cn('min-w-20 justify-center rounded-md', DISCIPLINE_CLASS[discipline])}>{t(`projects.discipline.${discipline}`)}</Badge>
}

export function RequirementBadge({ requirement }: { requirement: ModuleRequirement }) {
  const { t } = useTranslation()
  return (
    <Badge tone={requirement === 'required' ? 'danger' : 'neutral'} className="rounded-md">
      {t(`projects.requirement.${requirement}`)}
    </Badge>
  )
}

export function InclusionBadge({ requirement }: { requirement: ModuleRequirement }) {
  const { t } = useTranslation()
  return (
    <Badge tone={requirement === 'required' ? 'success' : 'info'} className="rounded-md">
      {t(requirement === 'required' ? 'projects.inclusion.included' : 'projects.inclusion.addable')}
    </Badge>
  )
}

export function ProgressBar({ value, className, label }: { value: number; className?: string; label: string }) {
  return (
    <div className={cn('flex items-center gap-3', className)}>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
        <div className="h-full rounded-full bg-primary transition-[width] duration-500" style={{ width: `${value}%` }} />
      </div>
      <span className="w-10 text-right text-xs font-medium tabular-nums text-muted-foreground">%{value}</span>
    </div>
  )
}

export function MemberStack({ names, max = 4 }: { names: string[]; max?: number }) {
  const { t } = useTranslation()
  if (names.length === 0) return <span className="text-xs text-muted-foreground">{t('projects.team.none')}</span>
  const rest = names.length - max
  return (
    <div className="flex items-center -space-x-1.5">
      {names.slice(0, max).map((n) => (
        <Avatar key={n} name={n} className="size-7 ring-2 ring-card [&>span]:text-[10px]" />
      ))}
      {rest > 0 ? (
        <span className="flex size-7 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground ring-2 ring-card">+{rest}</span>
      ) : null}
    </div>
  )
}
